import styles from './Card.module.css';

import classNames from 'classnames';

/**
 * **Card** — основной компонент-контейнер для отображения карточек с заголовком и содержимым.
 *
 * @component
 * @example
 * // Пример использования
 * <Card>
 *   <Card.Title text={'Циклы производства'}>
 *     <IconButton tooltip={'Настройки'} icon={settingsIcon} />
 *   </Card.Title>
 *   <Card.Content>
 *     <RegularButton icon={addIcon} variant="flat" text={'Переименовать'} />
 *     <SchemeEditor mode={schemeSelectedMode} initialNodes={selectedScheme?.nodes ?? []} />
 *     ...
 *   </Card.Content>
 * </Card>
 */
const Card = ({ children }) => {
  return <div className={styles.card}>{children}</div>;
};

/**
 * **Card.Title** — компонент для отображения заголовка карточки.
 *
 * Может содержать текстовые элементы и дополнительные дочерние элементы, такие как кнопки или иконки.
 *
 * @param {Object} props                     Свойства компонента.
 * @param {string} [props.text]              Текст заголовка.
 * @param {React.ReactNode} [props.children] Дочерние элементы, расположенные справа от текста.
 * @param {string} [props.className]         Пользовательский CSS-класс для кастомизации стилей.
 *
 * @component
 * @example
 * // Пример заголовка с дополнительной иконкой
 * <Card.Title text={'Циклы производства'}>
 *   <IconButton tooltip={'Настройки'} icon={settingsIcon} />
 * </Card.Title>
 */
Card.Title = function CardTitle({ className, children, text = undefined }) {
  return (
    <div className={classNames(className, styles.cardTitle)}>
      <span className={styles.titleText}>{text}</span>
      {children}
    </div>
  );
};

/**
 * **Card.Content** — компонент для отображения содержимого карточки.
 *
 * Выводит текст, кнопки или другие пользовательские компоненты в основной части карточки.
 *
 * @param {Object} props                     Свойства компонента.
 * @param {React.ReactNode} [props.children] Дочерние элементы, которые будут отображены внутри карточки.
 * @param {string} [props.className]         Пользовательский CSS-класс для кастомизации стилей.
 *
 * @component
 * @example
 * // Пример содержимого с кнопкой и редактором
 * <Card.Content>
 *   <RegularButton icon={addIcon} variant="flat" text={'Переименовать'} />
 *   <SchemeEditor mode={schemeSelectedMode} initialNodes={selectedScheme?.nodes ?? []} />
 * </Card.Content>
 */
Card.Content = function CardContent({ className, children }) {
  return <div className={classNames(className, styles.cardContent)}>{children}</div>;
};

export default Card;
